import { EXTERNAL_LINKS } from '@dpdgroupuk/cc-dpd-ui';
import { HttpStatusCode } from 'axios';
import PropTypes from 'prop-types';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { bindActionCreators } from 'redux';
import { createResponseInterceptor } from '../../apis/apis';
import { TOO_MANY_REQUESTS_MESSAGE } from '../../constants/strings';
import { FormatterHelpers } from '../../helpers';
import Error from '../../pages/Auth/SignIn/Error';

import Sentry, { getSentryUser } from '../../sentry';
import Loader from '../Loader';
import * as Actions from './actions';
import { AuthContext } from './Context';
import { isDpdUserAccount } from './model';
import { getAuthData } from './selectors';

const AuthProvider = props => {
  const dispatch = useDispatch();
  const { currentSession } = useSelector(getAuthData);
  const [isAppRestricted, setIsAppRestricted] = useState(false);
  const [retryAfter, setRetryAfter] = useState();
  const isSessionExpirationHandled = useRef(false);

  const hasDecodedIdToken = !!currentSession?.decodedIdToken;
  const shouldRedirectToLogon =
    hasDecodedIdToken && !isDpdUserAccount(currentSession);

  const value = useMemo(() => {
    const actions = bindActionCreators(Actions, dispatch);
    return {
      ...actions,
      currentSession,
    };
  }, [dispatch, currentSession]);

  useEffect(() => {
    Sentry.setUser(getSentryUser(currentSession?.uid));
  }, [currentSession?.uid]);

  useEffect(() => {
    if (shouldRedirectToLogon) {
      window.location.href = `${
        EXTERNAL_LINKS.LOGIN_URL
      }?redirectUrl=${encodeURIComponent(window.location.origin)}`;
    }
  }, [shouldRedirectToLogon]);

  useEffect(
    () => () => {
      isSessionExpirationHandled.current = false;
    },
    []
  );

  const fetchCurrentSession = useCallback(async () => {
    if (isAppRestricted) {
      return Promise.reject({
        code: HttpStatusCode.TooManyRequests,
        message: TOO_MANY_REQUESTS_MESSAGE(
          FormatterHelpers.formatTime(retryAfter)
        ),
      });
    }

    return await dispatch(Actions.getCurrentSession()).unwrap();
  }, [currentSession?.uid, isAppRestricted, retryAfter]);

  const handleErrorListener = useCallback(
    async error => {
      if (
        error.response &&
        error.response.status === 401 &&
        !isSessionExpirationHandled.current
      ) {
        isSessionExpirationHandled.current = true;
        await dispatch(Actions.getCurrentSession()).unwrap();
        dispatch({ type: 'auth/clear' });
      } else {
        if (
          error.response &&
          error.response.status === HttpStatusCode.TooManyRequests
        ) {
          setIsAppRestricted(true);
          setRetryAfter(error.response.data.error.retryAfter);
        }

        return Promise.reject(error);
      }
    },
    [dispatch]
  );

  useEffect(() => {
    createResponseInterceptor(response => response, handleErrorListener);
  }, []);

  if (shouldRedirectToLogon) {
    // ensure nothing renders while we are redirecting the user
    return null;
  }

  return (
    <AuthContext.Provider value={value}>
      <Loader
        promiseFn={fetchCurrentSession}
        watch={currentSession?.uid}
        errorComponent={error => <Error values={{ error }} />}
      >
        {props.children}
      </Loader>
    </AuthContext.Provider>
  );
};

AuthProvider.propTypes = {
  children: PropTypes.node,
};

export default AuthProvider;
